<template>
  <div class="h-20 w-full flex items-center relative py-2 pr-2">
    <button
      class="absolute top-0 -right-3 pt-2 z-10"
      @click="openQuestionModal"
    >
      <icon :icon="mdiAlertOctagonOutline" :size="IconSize.SM" />
    </button>

    <div class="h-full overflow-hidden flex gap-2 w-full">
      <div class="flex-shrink-0">
        <div v-if="hasFiles" class="flex gap-2 flex-wrap">
          <div
            v-if="question && question.type === QuestionType.FILE && answer"
            class="flex gap-2"
          >
            <survey-file-answer
              v-for="(fileUrl, idx) in answerFiles"
              :key="`answer-${idx}`"
              :file-url="fileUrl"
            />
          </div>
          <div v-if="questionFile" class="flex gap-2">
            <survey-file-answer
              v-for="(fileUrl, idx) in questionFiles"
              :key="`question-${idx}`"
              :file-url="fileUrl"
            />
          </div>
        </div>
        <div v-else-if="shouldShowImagePlaceholder" class="image-placeholder">
          <div
            class="h-16 w-16 bg-gray-lightest rounded flex items-center justify-center"
          />
        </div>
      </div>

      <div class="flex-1 min-w-0">
        <div
          v-if="question && question.type === QuestionType.TEXT"
          class="break-words whitespace-normal leading-none"
        >
          <expandable-text
            :text="formattedAnswer"
            :max-length="50"
            :hide-buttons="true"
          />
        </div>

        <div
          v-else-if="question && question.type === QuestionType.RADIO"
          class="flex"
        >
          <survey-radio-option v-if="answer" :label="formattedAnswer" />
          <span v-else class="leading-none">
            {{ $t('brand-surveys.answers.no-answer') }}
          </span>
        </div>

        <div
          v-else-if="question && question.type === QuestionType.CHECKBOX"
          class="flex flex-col gap-1"
        >
          <survey-checkbox-option
            v-for="option in answerOptions"
            :key="option"
            :label="option"
          />
          <span v-if="answerOptions.length === 0" class="leading-none">
            {{ $t('brand-surveys.details.no-options-selected') }}
          </span>
        </div>

        <div
          v-else-if="question && question.type === QuestionType.FILE"
          class="flex"
        >
          <span v-if="!answer" class="leading-none">
            {{ $t('brand-surveys.answers.no-answer') }}
          </span>
        </div>

        <span v-else>
          {{ formattedAnswer }}
        </span>
      </div>
    </div>
    <survey-question-modal
      :is-visible="showQuestionModal"
      :question="question"
      :answer="answer"
      :question-file="questionFile"
      :question-comment="questionComment"
      :question-index="answerIndex"
      :survey-internal-number="surveyInternalNumber"
      :user-full-name="userFullName"
      :survey-user-id="surveyUserId"
      :place-name="placeName"
      :place-id="placeId"
      :question-status="questionStatus"
      :survey-user="surveyUser"
      :is-editor="isEditor"
      :answers="allAnswers"
      @close="closeQuestionModal"
      @data-updated="handleDataUpdate"
    />
    <div
      v-if="canManageStatus"
      class="absolute bottom-0 -right-3 z-10 flex gap-2 pb-2"
    >
      <button class="text-green" @click="handleChangeStatus('APPROVED')">
        <icon :icon="mdiCheck" :size="IconSize.SM" />
      </button>
      <button class="text-red" @click="handleChangeStatus('REJECTED')">
        <icon :icon="mdiClose" :size="IconSize.SM" />
      </button>
      <button></button>
    </div>
  </div>
</template>

<script>
import { IconSize, Icon } from '@/components/Icon';
import { mdiAlertOctagonOutline, mdiImage, mdiCheck, mdiClose } from '@mdi/js';
import {
  QuestionType,
  updateSurveyUserQuestionStatus,
  SURVEY_ANSWER_SEPARATOR,
} from '@/api/v2/requests/surveys';
import { useRoleChecker } from '@/composables/use-role-checker';
import { SystemRole } from '@/api/requests/users';
import SurveyQuestionModal from './SurveyQuestionModal.vue';
import SurveyRadioOption from './SurveyRadioOption.vue';
import SurveyCheckboxOption from './SurveyCheckboxOption.vue';
import SurveyFileAnswer from './SurveyFileAnswer.vue';
import ExpandableText from '@/components/ExpandableText/ExpandableText.vue';

export default {
  name: 'SurveyAnswerCell',
  components: {
    Icon,
    SurveyQuestionModal,
    SurveyRadioOption,
    SurveyCheckboxOption,
    SurveyFileAnswer,
    ExpandableText,
  },
  setup(props, ctx) {
    const { checkAnyRole } = useRoleChecker(ctx);

    const canManageStatus = checkAnyRole([
      SystemRole.SUPER_ADMIN,
      SystemRole.STRUCTURE_MANAGER,
      SystemRole.BRAND_ADMIN,
    ]);

    return {
      canManageStatus,
    };
  },
  data() {
    return {
      mdiAlertOctagonOutline,
      mdiImage,
      mdiCheck,
      mdiClose,
      IconSize,
      QuestionType,
      showQuestionModal: false,
    };
  },
  computed: {
    surveyUser() {
      return this.params?.surveyUser;
    },
    question() {
      return this.params?.question;
    },
    answerIndex() {
      return this.params?.answerIndex;
    },
    answer() {
      return this.params?.data?.answers?.[this.answerIndex] || '';
    },
    questionFile() {
      return this.params?.data?.files?.[this.answerIndex] || '';
    },
    questionComment() {
      return this.params?.data?.comments?.[this.answerIndex] || '';
    },
    questionStatus() {
      return this.params?.data?.statuses?.[this.answerIndex] || '';
    },
    surveyInternalNumber() {
      return this.params?.surveyInternalNumber;
    },
    userFullName() {
      return this.params?.data?.user?.fullName;
    },
    surveyUserId() {
      return this.params?.data?.id;
    },
    allAnswers() {
      return this.params && this.params.data && this.params.data.answers
        ? this.params.data.answers
        : [];
    },
    placeName() {
      return this.params?.data?.user?.place?.name;
    },
    placeId() {
      return this.params?.data?.user?.place?.id;
    },
    dataUpdateCallback() {
      return this.params?.onDataUpdate;
    },
    isEditor() {
      return this.params?.isEditor || false;
    },
    hasFiles() {
      return (
        (this.question?.type === QuestionType.FILE && this.answer) ||
        this.questionFile
      );
    },
    shouldShowImagePlaceholder() {
      return (
        (this.question?.type === QuestionType.FILE &&
          !this.answer &&
          !this.questionFile) ||
        (this.question?.type !== QuestionType.FILE &&
          !this.questionFile &&
          this.couldHaveFile)
      );
    },
    couldHaveFile() {
      return (
        this.question &&
        [QuestionType.TEXT, QuestionType.RADIO, QuestionType.CHECKBOX].includes(
          this.question.type,
        )
      );
    },
    formattedAnswer() {
      if (!this.answer) {
        return this.$t('brand-surveys.answers.no-answer');
      }

      if (!this.question) {
        return this.answer;
      }

      switch (this.question.type) {
        case QuestionType.TEXT:
        case QuestionType.RADIO:
        case QuestionType.FILE:
          return this.answer;
        case QuestionType.CHECKBOX:
          return this.answer
            .split(SURVEY_ANSWER_SEPARATOR)
            .filter(Boolean)
            .join(', ');
        default:
          return this.answer;
      }
    },
    answerOptions() {
      if (
        !this.question ||
        this.question.type !== QuestionType.CHECKBOX ||
        !this.answer
      ) {
        return [];
      }
      return this.answer.split(SURVEY_ANSWER_SEPARATOR).filter(Boolean);
    },
    answerFiles() {
      if (!this.answer) return [];
      return this.answer.split(SURVEY_ANSWER_SEPARATOR).filter(Boolean);
    },
    questionFiles() {
      if (!this.questionFile) return [];
      return this.questionFile.split(SURVEY_ANSWER_SEPARATOR).filter(Boolean);
    },
  },
  methods: {
    openQuestionModal() {
      this.showQuestionModal = true;
    },
    closeQuestionModal() {
      this.showQuestionModal = false;
    },
    handleDataUpdate(updateData) {
      if (updateData && this.params && this.params.node && this.params.api) {
        const { questionIndex, newAnswer, newFiles } = updateData;

        if (questionIndex === this.answerIndex) {
          const rowNode = this.params.node;
          const data = { ...rowNode.data };

          if (!data.answers) data.answers = [];
          data.answers[questionIndex] = newAnswer;

          if (newFiles) {
            if (!data.files) {
              data.files = new Array(data.answers.length).fill('');
            }
            data.files = [...newFiles];
          }

          rowNode.setData(data);
          this.params.api.refreshCells({ rowNodes: [rowNode], force: true });
        }
      }

      if (this.dataUpdateCallback) {
        this.dataUpdateCallback();
      }
    },
    async handleChangeStatus(nextStatus) {
      if (nextStatus === this.questionStatus) nextStatus = 'IN_VERIFICATION';
      try {
        await updateSurveyUserQuestionStatus(
          this.surveyUserId,
          this.answerIndex,
          { status: nextStatus },
        );

        const rowNode = this.params.node;
        const data = { ...rowNode.data };
        const statuses = (data.statuses || []).slice();
        statuses[this.answerIndex] = nextStatus;
        rowNode.setData({ ...data, statuses });
        this.params.api.refreshCells({ rowNodes: [rowNode], force: true });
      } catch (err) {
        console.error(err);
      }
    },
  },
};
</script>

<style scoped>
.image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
